import React, { useState } from "react";
import { Percent } from "@phosphor-icons/react";

type CalcType = "inclusiveToExclusive" | "exclusiveToInclusive";

interface Result {
  label1: string;
  val1: number;
  kdvMiktari: number;
  gelirVergisi: number;
  label3: string;
  val3: number;
  rate: number;
}

function formatCurrency(num: number): string {
  return new Intl.NumberFormat("tr-TR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

function parseAmount(val: string): number {
  if (!val) return 0;
  return parseFloat(val.replace(/\./g, "").replace(",", ".")) || 0;
}

function formatInputValue(raw: string): string {
  let value = raw.replace(/[^0-9,]/g, "");
  const parts = value.split(",");
  if (parts.length > 2) value = parts[0] + "," + parts.slice(1).join("");

  if (value.includes(",")) {
    let [intPart, decPart] = value.split(",");
    intPart = intPart ? parseInt(intPart, 10).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".") : "0";
    decPart = decPart.slice(0, 2);
    return intPart + "," + decPart;
  } else {
    return value ? parseInt(value, 10).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".") : "";
  }
}

export default function KdvPage() {
  const [calcType, setCalcType] = useState<CalcType>("inclusiveToExclusive");
  const [amountInput, setAmountInput] = useState("");
  const [rate, setRate] = useState(20);
  const [result, setResult] = useState<Result | null>(null);

  function handleAmountChange(e: React.ChangeEvent<HTMLInputElement>) {
    setAmountInput(formatInputValue(e.target.value));
    setResult(null);
  }

  function handleCalcTypeChange(val: CalcType) {
    setCalcType(val);
    setResult(null);
  }

  function handleRateChange(r: number) {
    setRate(r);
    setResult(null);
  }

  function calculate() {
    const amount = parseAmount(amountInput);
    if (amount <= 0) return;

    let kdvDahil: number, kdvHaric: number, kdvMiktari: number, gelirVergisi: number;
    let label1: string, label3: string, val1: number, val3: number;

    if (calcType === "inclusiveToExclusive") {
      kdvDahil = amount;
      kdvHaric = kdvDahil / (1 + rate / 100);
      kdvMiktari = kdvDahil - kdvHaric;
      gelirVergisi = kdvHaric * 0.25;
      label1 = "KDV Dahil Tutar"; val1 = kdvDahil;
      label3 = "KDV Hariç Tutar"; val3 = kdvHaric;
    } else {
      kdvHaric = amount;
      kdvMiktari = kdvHaric * (rate / 100);
      kdvDahil = kdvHaric + kdvMiktari;
      gelirVergisi = kdvHaric * 0.25;
      label1 = "KDV Hariç Tutar"; val1 = kdvHaric;
      label3 = "KDV Dahil Tutar"; val3 = kdvDahil;
    }

    setResult({ label1, val1, kdvMiktari, gelirVergisi, label3, val3, rate });
  }

  function clearForm() {
    setAmountInput("");
    setResult(null);
  }

  const amountLabel = calcType === "inclusiveToExclusive" ? "KDV Dahil Tutar" : "KDV Hariç Tutar";

  return (
    <div className="kdv-page">
      <div className="kdv-glass-panel">

        {/* Başlık */}
        <div className="kdv-header">
          <Percent size={28} weight="duotone" />
          <span style={{ fontWeight: 600, fontSize: "1.1rem" }}>KDV Hesaplama</span>
        </div>

        <div className="kdv-body">

        {/* Sol Panel */}
        <form
          className="kdv-left"
          onSubmit={(e) => {
            e.preventDefault();
            if (amountInput && !result) calculate();
          }}
        >

          {/* Radio Seçenekleri */}
          <div className="kdv-radios">
            {(["inclusiveToExclusive", "exclusiveToInclusive"] as CalcType[]).map((type) => (
              <label key={type} className="kdv-radio-label">
                <input
                  type="radio"
                  name="calcType"
                  className="kdv-radio"
                  checked={calcType === type}
                  onChange={() => handleCalcTypeChange(type)}
                />
                {type === "inclusiveToExclusive"
                  ? "KDV Dahil Tutardan KDV Hariç Tutar Hesapla"
                  : "KDV Hariç Tutardan KDV Dahil Tutar Hesapla"}
              </label>
            ))}
          </div>

          {/* Tutar Girişi */}
          <div className="kdv-field">
            <label className="kdv-field-label">{amountLabel}</label>
            <div className="kdv-input-wrap">
              <span className="kdv-currency">₺</span>
              <input
                type="text"
                className="kdv-input"
                placeholder="0,00"
                value={amountInput}
                onChange={handleAmountChange}
                autoFocus
              />
            </div>
          </div>

          {/* KDV Oranı */}
          <div className="kdv-field">
            <label className="kdv-field-label">KDV Oranı</label>
            <div className="kdv-segments">
              {[20, 10, 1].map((r) => (
                <button
                  key={r}
                  type="button"
                  className={`kdv-seg-btn ${rate === r ? "active" : ""}`}
                  onClick={() => handleRateChange(r)}
                >
                  %{r}
                </button>
              ))}
            </div>
          </div>

          {/* Butonlar */}
          <div className="kdv-actions">
            <button
              type="submit"
              className={`kdv-btn-primary ${!amountInput || result ? "kdv-btn-disabled" : ""}`}
              disabled={!amountInput || !!result}
            >
              Hesapla
            </button>
            <button type="button" className="kdv-btn-secondary" onClick={clearForm}>
              Temizle
            </button>
          </div>
        </form>

        {/* Sağ Panel */}
        <div className="kdv-right">
          <div className="kdv-result-card">
            <h2 className="kdv-result-title">Hesaplama Sonucu</h2>

            {!result ? (
              <p className="kdv-result-empty">
                Sonucu görmek için tutar girip "Hesapla" butonuna basın.
              </p>
            ) : (
              <div className="kdv-result-rows">
                <div className="kdv-result-row">
                  <span className="kdv-result-row-label">{result.label1}</span>
                  <span className="kdv-result-row-val">₺{formatCurrency(result.val1)}</span>
                </div>
                <div className="kdv-result-row">
                  <span className="kdv-result-row-label">
                    KDV Miktarı <span className="kdv-result-rate">(%{result.rate})</span>
                  </span>
                  <span className="kdv-result-row-val">₺{formatCurrency(result.kdvMiktari)}</span>
                </div>
                <div className="kdv-result-row muted">
                  <span className="kdv-result-row-label">Gelir Vergisi <span className="kdv-result-rate">(%25)</span></span>
                  <span className="kdv-result-row-val">₺{formatCurrency(result.gelirVergisi)}</span>
                </div>
                <hr className="kdv-divider" />
                <div className="kdv-result-row total">
                  <span className="kdv-result-row-label">{result.label3}</span>
                  <span className="kdv-result-row-val accent">₺{formatCurrency(result.val3)}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        </div> {/* kdv-body */}
      </div>
    </div>
  );
}
